import { useState } from 'react'

import { Box, Button, Section, Text } from '@factorialco/deprecated-design-system'
import {
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvanceAccrualModeSettingDocument,
  type SaveSalaryAdvanceAccrualModeSettingMutation,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import Select from 'modules/core/components/Select'

import {
  ACCRUAL_MODE_ATTENDANCE_CONNECTED,
  ACCRUAL_MODE_CALENDAR_ONLY,
  type AccrualMode,
  isAccrualMode,
} from 'modules/salary_advance/lib/companySettings'

type Props = {
  accrualMode: string
  timeTrackingAvailableForAccrualMode: boolean
  canEdit: boolean
}

const AccrualModeSetting = ({
  accrualMode,
  timeTrackingAvailableForAccrualMode,
  canEdit,
}: Props) => {
  const [saveAccrualMode, { loading }] = useMutation(SaveSalaryAdvanceAccrualModeSettingDocument, {
    extractorFn: (payload: SaveSalaryAdvanceAccrualModeSettingMutation) =>
      payload.salaryAdvanceMutations?.saveAccrualModeSalaryadvanceCompanySetting ?? { errors: [] },
    refetchQueries: [SalaryAdvanceSettingsPageDocument],
  })
  const [isConfirmationOpen, setConfirmationOpen] = useState(false)
  const [pendingAccrualMode, setPendingAccrualMode] = useState<AccrualMode | null>(null)

  const currentAccrualMode = isAccrualMode(accrualMode) ? accrualMode : null
  const hasUnsupportedAccrualMode = currentAccrualMode === null
  const isAttendanceConnected = currentAccrualMode === ACCRUAL_MODE_ATTENDANCE_CONNECTED
  const modeOptions = [
    {
      value: ACCRUAL_MODE_CALENDAR_ONLY,
      label: i18n.t('salary_advance.settings.accrual_mode.calendar_label'),
    },
    {
      value: ACCRUAL_MODE_ATTENDANCE_CONNECTED,
      label: i18n.t('salary_advance.settings.accrual_mode.attendance_label'),
    },
  ]

  const handleSelectMode = (nextValue: string) => {
    if (!currentAccrualMode || !isAccrualMode(nextValue)) return

    if (nextValue === currentAccrualMode) return

    if (nextValue === ACCRUAL_MODE_ATTENDANCE_CONNECTED && !timeTrackingAvailableForAccrualMode) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('salary_advance.settings.accrual_mode.requires_time_tracking'),
      })
      return
    }

    setPendingAccrualMode(nextValue)
    setConfirmationOpen(true)
  }

  const closeConfirmation = () => {
    setConfirmationOpen(false)
    setPendingAccrualMode(null)
  }

  const handleConfirm = async () => {
    if (!pendingAccrualMode) return

    const result = await saveAccrualMode({
      variables: {
        accrualMode: pendingAccrualMode,
      },
    })

    const handledResult = handleResult(result, () => {})

    track(trackingEvents.salaryAdvance.settingsSaveResult, {
      setting_key: 'accrual_mode',
      setting_value: pendingAccrualMode,
      outcome: handledResult.success ? 'success' : 'failure',
    })

    if (handledResult.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closeConfirmation()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }
  const modeControlDisabled =
    !canEdit ||
    loading ||
    hasUnsupportedAccrualMode ||
    (!timeTrackingAvailableForAccrualMode && !isAttendanceConnected)

  return (
    <>
      <Section.Form
        illustration="clock-app"
        title={i18n.t('salary_advance.settings.accrual_mode.title')}
        description={i18n.t('salary_advance.settings.accrual_mode.description')}
        isDirty={false}
      >
        <Box flexDirection="column" gap="s12">
          <Select
            none={i18n.t('common.select')}
            value={currentAccrualMode ?? ''}
            onChange={handleSelectMode}
            options={modeOptions}
            missingLabel={i18n.t('common.missing_field')}
            readonly={modeControlDisabled}
          />

          <Text size="100" className="text-f1-foreground-secondary">
            {currentAccrualMode === ACCRUAL_MODE_ATTENDANCE_CONNECTED
              ? i18n.t('salary_advance.settings.accrual_mode.attendance_description')
              : currentAccrualMode === ACCRUAL_MODE_CALENDAR_ONLY
                ? i18n.t('salary_advance.settings.accrual_mode.calendar_description')
                : i18n.t('salary_advance.settings.accrual_mode.unsupported_mode')}
          </Text>

          {!timeTrackingAvailableForAccrualMode && (
            <Text size="100" className="text-f1-foreground-secondary">
              {i18n.t('salary_advance.settings.accrual_mode.requires_time_tracking')}
            </Text>
          )}
        </Box>
      </Section.Form>

      {isConfirmationOpen && (
        <Modal
          illustration="alert"
          title={i18n.t('salary_advance.settings.accrual_mode.confirmation_title')}
          description={i18n.t('salary_advance.settings.accrual_mode.confirmation_description')}
          onClose={closeConfirmation}
          footer={
            <>
              <Button type="secondary" onClick={closeConfirmation}>
                {i18n.t('common.cancel')}
              </Button>
              <Button onClick={handleConfirm} disabled={loading}>
                {i18n.t('common.confirm')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}

export default AccrualModeSetting
